/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/step.scss';
import {
    AwesomeIcon,
    ElementPosition,
    Link,
    Loader,
    MenuPlacement,
    OptionsMenu,
    OptionsMenuItem,
    ResponseData,
    SelectItemShape
} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import EmailStep, {EmailStepFields} from '../../../models/emailStepModel';

import EmailTemplate from '../../../models/emailTemplateModel';

import {hasAdminLogin} from '../../../util/adminUtil';

import {translateEmailStepError} from '../../../util/bolValidation';

import {EMAIL_SETTINGS_ROUTE} from '../../../util/constants';

import {
    getAttachmentsLabel,
    getCustomerTypeItems,
    getDayItems,
    getProductsLabel,
    getTemplateLabel
} from '../../../util/emailStepUtil';

import {AccessKey, ActivateHandle, EmailStatLoader, EmailStepStatTotal, Fulfilment} from '../../../util/interfaces';

import {onShowConfirmSnackbar} from '../../../util/renderUtil';

import {EmailStepError, ProductSelection} from '../../../util/types';

import {createErrorMessage, numberTrim, onNavigate} from '../../../util/utils';

import {BluemateInput, BluemateSelect} from '../../controls';

import CountrySelectController from '../../controls/country/select/countrySelect/countrySelectController';

import {AppContext} from '../../misc/appContext';

import useRight from '../../misc/useRight';

import ProductSelectionController from '../../productSelection/productSelectionController';

import AttachmentSelectController from '../attachment/attachmentSelectController';

import DaysSelectController from '../day/daysSelectController';

import TestMailController, {TestMailRef} from '../test/testMailController';

import EmailAdminInfoController from './adminInfo/emailAdminInfoController';

import EmailFlowErrors from '../emailFlowErrors';

import WarnIcon from '../../icon/warnIcon';

import {showDialog, showSnackbar} from '@dvrd/dvr-controls';

interface Props {
    onReplaceStep: (step: EmailStep) => void;
    onDeleteStep?: (step: EmailStep) => void;
    onOpenStats?: (step: EmailStep) => MouseEventHandler;
    onGlobalLoad?: (loading: boolean) => void;
    onClickTemplate: MouseEventHandler;
    allowChange?: (step: EmailStep, boolOnly?: boolean) => boolean;
    onNavigateTemplate?: (template: EmailTemplate, step: EmailStep, returnPage?: string) => void;
    onLoadStepStats?: (loader: EmailStatLoader) => void;
    onActivationChanged?: VoidFunction;
    step: EmailStep;
    templates: EmailTemplate[];
    index?: number;
    showWarning: boolean;
    className?: string;
    dayItems?: SelectItemShape[];
    errors?: Array<EmailStepError> | null;
}

export default function Step(props: Props) {
    const {t} = useTranslation();
    const fulfilmentItems: SelectItemShape[] = [
        {label: t('email.step.fulfilment.both', 'Alle logistiek'), value: Fulfilment.BOTH},
        {label: t('email.step.fulfilment.fbr', 'Eigen logistiek'), value: Fulfilment.FBR},
        {label: t('email.step.fulfilment.fbb', 'LVB'), value: Fulfilment.FBB}
    ];
    const customerTypeItems = getCustomerTypeItems();
    const context = useContext(AppContext);
    const [canEdit, canTestMail, canOpenStats] = useRight(
        AccessKey.EMAIL_EDIT,
        AccessKey.EMAIL_TEST_MAIl,
        AccessKey.EMAIL_STATS
    );
    const {
        step,
        onDeleteStep,
        onReplaceStep,
        templates,
        index,
        onOpenStats,
        showWarning,
        className,
        dayItems,
        onClickTemplate,
        errors,
        onGlobalLoad,
        onNavigateTemplate,
        onLoadStepStats,
        onActivationChanged
    } = props;
    const [loading, setLoading] = useState(false);
    const [loadingStats, setLoadingStats] = useState(true);
    const [stats, setStats] = useState<EmailStepStatTotal>({CLICK: 0, SENT: 0, OPEN: 0});
    const [editingLabel, setEditingLabel] = useState(false);
    const attachmentRef = useRef<ActivateHandle>(null);
    const productRef = useRef<ActivateHandle>(null);
    const daysRef = useRef<ActivateHandle>(null);
    const countryRef = useRef<ActivateHandle>(null);
    const testMailRef = useRef<TestMailRef>(null);
    const flowErrorsRef = useRef<ActivateHandle>(null);
    const allowChange =
        props.allowChange ??
        function () {
            return true;
        };

    function onEditLabel() {
        setEditingLabel(true);
    }

    function onChangeLabel(value: string) {
        step.label = value;
        onReplaceStep(step);
    }

    function onSubmitLabel() {
        setLoading(true);
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                setEditingLabel(false);
            } else handleErrorResponse(data);
        });
    }

    function onClickCountries() {
        if (allowChange(step)) countryRef.current?.activate(step.countries);
    }

    function onClickAttachment() {
        if (allowChange(step)) attachmentRef.current?.activate(step);
    }

    function onClickProduct() {
        if (!allowChange(step)) return;
        const selection = step.productSelectionJSON;
        productRef.current?.activate(selection);
    }

    function onChangeDays(value: string | number) {
        if (!allowChange(step)) return;
        if (value === 'custom') return daysRef.current?.activate(step);
        else if (typeof value === 'number') onChangeStep('days')(value);
    }

    function onCustomDays(days: number, onlyWorkDays: boolean) {
        if (!allowChange(step)) return;
        const _step = step.clone();
        _step.days = days;
        _step.onlyWorkDays = onlyWorkDays;
        setLoading(true);
        _step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            if (success) {
                setLoading(false);
                onReplaceStep(step);
                onShowConfirmSnackbar(t('email.step.emailUpdated', 'De e-mail is bijgewerkt'));
            } else handleErrorResponse(data);
        });
    }

    function onChangeStep(key: keyof EmailStepFields) {
        return function (value: any) {
            if (!allowChange(step)) return;
            const _step = step.clone();
            _step.setField(key, value);
            setLoading(true);
            _step.update((step: EmailStep, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    onReplaceStep(step);
                    onShowConfirmSnackbar(t('email.step.emailUpdated', 'De e-mail is bijgewerkt'));
                } else handleErrorResponse(data);
            });
        };
    }

    function onChangeCountries(countries: Set<string>, allSelected: boolean, callback: (close: boolean) => void) {
        if (!allowChange(step)) return;
        const _step = step.clone();
        if (allSelected) _step.countries = [];
        else _step.countries = Array.from(countries);
        setLoading(true);
        _step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                onShowConfirmSnackbar(t('email.step.emailUpdated', 'De e-mail is bijgewerkt'));
            } else handleErrorResponse(data);
            callback(!!data.success);
        });
    }

    function onSubmitProductSelection(selection: ProductSelection) {
        if (!allowChange(step)) return productRef.current?.close!();
        const _step = step.clone();
        _step.productSelectionJSON = selection;
        if (!_step.productSelectionChanged) return productRef.current?.close!();
        setLoading(true);
        _step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                onShowConfirmSnackbar(t('email.step.emailUpdated', 'De e-mail is bijgewerkt'));
                productRef.current?.close!();
            } else handleErrorResponse(data);
        });
    }

    function onClickEdit() {
        if (!allowChange(step)) return;
        if (onNavigateTemplate && step.template) return onNavigateTemplate(step.template, step);
        if (!step.template)
            showDialog(createErrorMessage(t('email.step.noTemplate', 'Je hebt nog geen e-mail template gekozen.')));
    }

    function onClickTestSend() {
        if (!step.template)
            return showDialog(
                createErrorMessage(t('email.step.noTemplate', 'Je hebt nog geen e-mail template gekozen.'))
            );
        testMailRef.current?.activate();
    }

    function onCloseTestAddress() {
        testMailRef.current?.onClose();
    }

    function onSendTestMail(recipient: string) {
        if (!step.template) return;
        testMailRef.current?.setLoading(true);
        step.template.test(recipient, step.id, (_: EmailTemplate, success: boolean, data: ResponseData) => {
            testMailRef.current?.setLoading(false);
            if (success) {
                onCloseTestAddress();
                showSnackbar(t('email.step.testMailSent', 'De testmail is verzonden'));
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('email.step.testMailFailed', 'Het verzenden van de testmail is niet gelukt.')
                    ),
                    t('email.step.testMailFailedTitle', 'Verzenden mislukt')
                );
        });
    }

    function getStepOptionsMenu(): OptionsMenuItem[] {
        const items: OptionsMenuItem[] = [];
        if (canEdit) {
            items.push({
                label: t('email.step.editName', 'Naam wijzigen'),
                onClick: onEditLabel,
                icon: <AwesomeIcon name='edit' className='icon' />
            });
            if (props.onNavigateTemplate !== undefined)
                items.push({
                    label: t('email.step.editContent', 'Inhoud wijzigen'),
                    onClick: onClickEdit,
                    icon: <AwesomeIcon name='pen' className='icon' />
                });
        }
        if (canTestMail)
            items.push({
                label: t('email.step.testSend', 'Test verzenden'),
                onClick: onClickTestSend,
                icon: <AwesomeIcon name='paper-plane' className='icon' />
            });
        items.push({
            label: step.active
                ? t('email.step.pauseSending', 'Zending pauzeren')
                : t('email.step.enableSending', 'Zending inschakelen'),
            onClick: onClickEnable,
            icon: <AwesomeIcon name={step.active ? 'pause' : 'play'} className='icon' />
        });
        if (onDeleteStep && canEdit)
            items.push({
                label: t('email.step.delete', 'Verwijderen'),
                onClick: onClickDelete,
                icon: <AwesomeIcon name='trash' className='icon' />
            });
        return items;
    }

    function onClickDelete() {
        showDialog(
            t('email.step.deleteConfirm', 'Weet je zeker dat je deze e-mail wilt verwijderen?'),
            t('email.step.deleteTitle', 'E-mail verwijderen'),
            [t('email.step.no', 'Nee'), {label: t('email.step.yes', 'Ja'), onClick: onConfirmDelete, primary: false}]
        );
    }

    function onConfirmDelete() {
        if (!onDeleteStep) return;
        if (onGlobalLoad) onGlobalLoad(true);
        setLoading(true);
        step.delete((step: EmailStep, success: boolean, data: ResponseData) => {
            if (onGlobalLoad) onGlobalLoad(false);
            setLoading(false);
            if (success) {
                showSnackbar(t('email.step.deleted', 'De e-mail is verwijderd'));
                onDeleteStep(step);
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('email.step.deleteFailed', 'Het verwijderen van de stap is niet gelukt.')
                    ),
                    t('email.step.deleteFailedTitle', 'Verwijderen mislukt')
                );
        });
    }

    function onClickEnable() {
        setLoading(true);
        const _step = step.clone();
        _step.active = !_step.active;
        _step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                onActivationChanged?.();
                const action = step.active
                    ? t('email.step.actionEnabled', 'ingeschakeld')
                    : t('email.step.actionPaused', 'gepauzeerd');
                showSnackbar(`${t('email.step.emailIs', 'De e-mail is')} ${action}`);
            } else handleErrorResponse(data);
        });
    }

    function handleErrorResponse(data: ResponseData) {
        const errors = data.errors;
        if (errors) flowErrorsRef.current?.activate(errors, t('email.step.updating', 'bijwerken'));
        else
            showDialog(
                createErrorMessage(
                    data.message ?? t('email.step.updateFailed', 'Het bijwerken van de e-mail is niet gelukt.')
                ),
                t('email.step.updateFailedTitle', 'Bijwerken mislukt')
            );
    }

    function renderLabel() {
        if (editingLabel) return renderLabelEditor();
        return (
            <label className='step-title' onClick={onEditLabel}>
                {step.label}
                {renderIsNew(isNew)}
                {!step.active && <span className='inactive-label'>{t('email.step.pausedSuffix', '(gepauzeerd)')}</span>}
                <AwesomeIcon name='edit' className='edit-icon' title={t('email.step.editName', 'Naam wijzigen')} />
            </label>
        );
    }

    function renderLabelEditor() {
        return (
            <BluemateInput
                onChange={onChangeLabel}
                value={step.label}
                onEnter={onSubmitLabel}
                className='label-editor'
                ornaments={{
                    element: <AwesomeIcon name='check' onClick={onSubmitLabel} className='submit-check' />,
                    placement: ElementPosition.RIGHT
                }}
                autoFocus
                autoSelect
            />
        );
    }

    function renderIndex() {
        if (!index) return null;
        return (
            <>
                {index > 1 && <div className='line top' />}
                <div className='line bottom' />
                <div className='index-container'>
                    <AwesomeIcon name='envelope' />
                    {/*<span className='index'>{index}</span>*/}
                </div>
            </>
        );
    }

    function renderVarButton(label: string, onClick: MouseEventHandler, isRequired: boolean = false) {
        const clickable = !loading && canEdit;
        return (
            <div
                className={classNames(
                    'var-button',
                    isRequired && 'required',
                    loading && 'disabled',
                    !canEdit && 'forbidden'
                )}
                onClick={clickable ? onClick : undefined}
            >
                <label className='button-label'>{label}</label>
            </div>
        );
    }

    function renderDaysSection() {
        const selectValue = [-1, 0, 1, 3, 7, 14].includes(step.days) ? step.days : 'custom',
            items = dayItems ?? getDayItems(step, context.accountContext.account);
        return (
            <div className='section'>
                <label className='text'>{t('email.step.after', 'Na')}</label>
                <BluemateSelect
                    value={selectValue}
                    onChange={onChangeDays}
                    items={items}
                    className={classNames('select-field', !canEdit && 'forbidden')}
                    canOpen={() => allowChange(step)}
                    disabled={loading || !canEdit}
                />
            </div>
        );
    }

    function renderProductsSection() {
        const {productSelectionJSON, productSelection, numProducts} = step;
        const isRequired = !productSelectionJSON.clauses.length;
        return (
            <div className='section'>
                <label className='text'>{t('email.step.for', 'voor')}</label>
                {renderVarButton(
                    getProductsLabel(productSelectionJSON.clauses, productSelection, numProducts, step.active),
                    onClickProduct,
                    isRequired
                )}
            </div>
        );
    }

    function renderTemplateSection() {
        const isRequired = step.templateId === '';
        return (
            <div className='section'>
                <label className='text'>{t('email.step.send', 'stuur')}</label>
                {renderVarButton(getTemplateLabel(step, templates), onClickTemplate, isRequired)}
            </div>
        );
    }

    function renderAttachmentSection() {
        return (
            <div className='section'>
                <label className='text'>{t('email.step.mailWithAttachment', 'mail met bijlage')}</label>
                {renderVarButton(getAttachmentsLabel(step), onClickAttachment)}
            </div>
        );
    }

    function renderCustomerSection() {
        return (
            <div className='section'>
                <label className='text'>{t('email.step.to', 'naar')}</label>
                <BluemateSelect
                    value={step.customerType}
                    onChange={onChangeStep('customer_type')}
                    items={customerTypeItems}
                    className={classNames('select-field', !canEdit && 'forbidden')}
                    canOpen={() => allowChange(step)}
                    disabled={loading || !canEdit}
                />
            </div>
        );
    }

    function renderCountry() {
        const count = step.countries.length;
        let label: string;
        if (!count) label = t('email.step.allCountries', 'Alle landen');
        else if (count === 1) label = step.countries[0];
        else label = `${count} ${t('email.step.countries', 'landen')}`;
        return (
            <div className='section'>
                <label className='text'>{t('email.step.in', 'in')}</label>
                {renderVarButton(label, onClickCountries)}
            </div>
        );
    }

    function renderFulfilment() {
        return (
            <div className='section'>
                <label className='text'>{t('email.step.via', 'via')}</label>
                <BluemateSelect
                    value={step.fulfilment}
                    onChange={onChangeStep('fulfilment')}
                    items={fulfilmentItems}
                    className={classNames('select-field', !canEdit && 'forbidden')}
                    canOpen={() => allowChange(step)}
                    disabled={loading || !canEdit}
                />
            </div>
        );
    }

    function loadStats() {
        if (!onOpenStats || !onLoadStepStats) return;
        setLoadingStats(true);
        onLoadStepStats({
            id: step.id,
            callback: (data: ResponseData) => {
                setLoadingStats(false);
                if (data.success)
                    setStats({
                        CLICK: data.click_count,
                        SENT: data.sent_count,
                        OPEN: data.open_count
                    });
                else
                    showDialog(
                        createErrorMessage(
                            data.message ?? t('email.step.statsFailed', 'Het laden van de statistieken is niet gelukt.')
                        ),
                        t('email.step.statsFailedTitle', 'Laden mislukt')
                    );
            }
        });
    }

    function renderStatsButton() {
        const {SENT, OPEN, CLICK} = stats;
        if (!onOpenStats) return;
        return (
            <div
                className={classNames('email-stats-button', !canOpenStats && 'forbidden')}
                onClick={canOpenStats ? onOpenStats(step) : undefined}
            >
                <Loader active={loadingStats} size='3rem' />
                <div className='stat-container'>
                    <label>{t('email.step.stat.sent', 'Verstuurd')}</label>
                    <label>{loadingStats ? '-' : numberTrim(SENT)}</label>
                </div>
                <AwesomeIcon name='arrow-up-right-from-square' className='icon-container' />
                <div className='stat-container'>
                    <label>{t('email.step.stat.open', 'Open')}</label>
                    <label>{loadingStats ? '-' : SENT === 0 ? 0 : ((OPEN / SENT) * 100).toFixed(1)}%</label>
                </div>
                <div className='stat-container'>
                    <label>{t('email.step.stat.click', 'Klik')}</label>
                    <label>{loadingStats ? '-' : numberTrim(CLICK)}</label>
                </div>
            </div>
        );
    }

    function renderStats() {
        if (!onOpenStats) return null;
        const isAdmin = context.userContext.user?.isAdmin() === true || hasAdminLogin();
        const statsDisabled = !isAdmin && context.accountContext.account?.getFlex('use_no_reply_tracking') !== true;
        return (
            <div className={classNames('step-info', showWarning && 'with-warning')}>
                {statsDisabled ? (
                    <div className='stats-disabled' onClick={onNavigate(EMAIL_SETTINGS_ROUTE)}>
                        <label className='text'>{t('email.step.statsOff', 'Statistieken staan uit...')}</label>
                    </div>
                ) : (
                    renderStatsButton()
                )}
                {renderWarn()}
            </div>
        );
    }

    function renderWarn() {
        if (!showWarning) return;
        let _errors: Array<string | null> | null;
        if (errors) _errors = errors.map((error: EmailStepError) => translateEmailStepError(error, false));
        else
            _errors = [
                t(
                    'email.step.overlapError',
                    'Er wordt al een andere e-mail naar deze klanten verzonden. Stuur maximaal 1 mail per 1 order.'
                )
            ];
        const hasOverlapError = errors?.some((err: EmailStepError) => err.reason === 'overlap');
        return (
            <div className='step-warning'>
                <WarnIcon />
                <div className='description'>
                    {!!_errors.length && (
                        <ul>
                            {_errors.map((err: string | null, index: number) => {
                                if (!err) return null;
                                return <li key={index}>{err}</li>;
                            })}
                        </ul>
                    )}
                    {hasOverlapError && (
                        <Link
                            className='info-link'
                            route='https://partnerplatform.bol.com/nl/hulp-nodig/prestaties/beleidspunten-zelfpromotie/'
                        >
                            {t('email.step.moreInfo', 'Meer informatie')}
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    function renderQuickActions() {
        return (
            <OptionsMenu
                options={getStepOptionsMenu()}
                placement={MenuPlacement.BOTTOM_LEFT}
                optionsComponent={<AwesomeIcon name='gear' className='step-options-icon icon' />}
                containerClass='common-options'
            />
        );
    }

    function renderIsNew(isNew: boolean) {
        if (!isNew) return null;
        return <span className='new-email-tag'>{t('email.step.new', 'Nieuw')}</span>;
    }

    useEffect(() => {
        loadStats();
    }, [step.id]);

    const isNew = step.getFlex('new') === true;
    return (
        <>
            <div className={classNames('email-step', !step.active && 'inactive', className)}>
                <EmailAdminInfoController emailStepID={step.id} />
                {renderLabel()}
                {renderIndex()}
                <div className={classNames('step-settings', isNew && 'new')}>
                    <Loader active={loading} size='3rem' />
                    {renderDaysSection()}
                    {renderProductsSection()}
                    {renderTemplateSection()}
                    {renderAttachmentSection()}
                    {renderCustomerSection()}
                    {renderCountry()}
                    {renderFulfilment()}
                </div>
                {renderStats()}
                {renderQuickActions()}
            </div>
            <AttachmentSelectController onReplaceStep={onReplaceStep} ref={attachmentRef} />
            <ProductSelectionController loading={loading} onSubmit={onSubmitProductSelection} ref={productRef} />
            <DaysSelectController onSubmit={onCustomDays} ref={daysRef} />
            <TestMailController onSubmit={onSendTestMail} ref={testMailRef} closeOnSubmit={false} />
            <CountrySelectController
                ref={countryRef}
                onSubmit={onChangeCountries}
                loading={loading}
                splitBelgiumLanguage
            />
            <EmailFlowErrors ref={flowErrorsRef} />
        </>
    );
}
